import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import { SettingsNavigation, SettingsTab } from '../../components/admin/settings/SettingsNavigation';
import { SettingsLayout } from '../../components/admin/settings/SettingsLayout';
import { DeveloperToolsSection } from '../../components/admin/settings/DeveloperToolsSection';

// Import des sections existantes
import SystemConfigSection from '../../components/admin/SystemConfigSection';
import ApiConfigSection from '../../components/admin/ApiConfigSection';
import AiPromptsSection from '../../components/admin/AiPromptsSection';
import ExcelImportSection from '../../components/admin/ExcelImportSection';
import SeoConfigSection from '../../components/admin/SeoConfigSection';
import RobotsTxtConfigSection from '../../components/admin/RobotsTxtConfigSection';
import SecurityConfigSection from '../../components/admin/SecurityConfigSection';
import DataForSEODebugger from '../../components/admin/DataForSEODebugger';

// Import des hooks
import { useSettings } from '../../hooks/useSettings';

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const { settings, updateSettings, isLoading, error } = useSettings();
  const [isSaving, setIsSaving] = useState(false);

  // États locaux pour les différentes sections
  const [systemConfig, setSystemConfig] = useState(settings?.systemConfig || {
    domain: 'https://app.smart-impact.ch',
    invalidTokenRedirect: 'https://smart-impact.ch',
    tokenPrefix: '/t/'
  });

  const [aiPrompts, setAiPrompts] = useState(settings?.aiPrompts || {
    companySummary: '',
    recommendations: '',
    digitalScore: ''
  });

  const [seoConfig, setSeoConfig] = useState(settings?.seoConfig || {
    testMode: false,
    metaNoIndex: false,
    defaultTitle: 'Cold Sales System',
    defaultDescription: '',
    defaultKeywords: '',
    ogSiteName: '',
    ogImage: '',
    twitterSite: '',
    sitemapRoutes: ''
  });

  const [robotsConfig, setRobotsConfig] = useState(settings?.robotsConfig || {
    content: '',
    active: true
  });

  // Mettre à jour les états locaux quand les settings sont chargés
  useEffect(() => {
    if (settings) {
      setSystemConfig(settings.systemConfig);
      setAiPrompts(settings.aiPrompts);
      setSeoConfig(settings.seoConfig);
      setRobotsConfig(settings.robotsConfig);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      if (!settings) return;
      
      const updatedSettings = {
        ...settings,
        systemConfig,
        aiPrompts,
        seoConfig,
        robotsConfig
      };
      
      await updateSettings(updatedSettings);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <SettingsLayout
            title="Configuration générale"
            description="Paramètres généraux du système"
            onSave={handleSave}
            isSaving={isSaving}
            isLoading={isLoading}
          >
            <div className="space-y-8">
              <SystemConfigSection 
                systemConfig={systemConfig}
                setSystemConfig={setSystemConfig}
                onSave={handleSave}
              />
              <ExcelImportSection />
            </div>
          </SettingsLayout>
        );

      case 'apis':
        return (
          <SettingsLayout
            title="Configuration des APIs"
            description="Gestion des intégrations API et des prompts IA"
            onSave={handleSave}
            isSaving={isSaving}
            isLoading={isLoading}
          >
            <div className="space-y-8">
              <ApiConfigSection />
              <AiPromptsSection 
                aiPrompts={aiPrompts}
                setAiPrompts={setAiPrompts}
                onSave={handleSave}
              />
              <DataForSEODebugger />
            </div>
          </SettingsLayout>
        );

      case 'security':
        return (
          <SettingsLayout
            title="Sécurité"
            description="Configuration de la sécurité et des accès"
            isLoading={isLoading}
          >
            <SecurityConfigSection />
          </SettingsLayout>
        );

      case 'seo':
        return (
          <SettingsLayout
            title="Configuration SEO"
            description="Paramètres SEO et robots.txt"
            onSave={handleSave}
            isSaving={isSaving}
            isLoading={isLoading}
          >
            <div className="space-y-8">
              <SeoConfigSection 
                config={seoConfig}
                onConfigChange={setSeoConfig}
                onSave={handleSave}
              />
              <RobotsTxtConfigSection 
                config={robotsConfig}
                onConfigChange={setRobotsConfig}
                onSave={handleSave}
              />
            </div>
          </SettingsLayout>
        );

      case 'development':
        return (
          <SettingsLayout
            title="Outils de développement"
            description="Outils et ressources pour les développeurs"
            isLoading={isLoading}
          >
            <DeveloperToolsSection />
          </SettingsLayout>
        );

      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <SettingsNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Contenu principal */}
        <main className="py-6">
          {error ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Erreur lors du chargement des paramètres
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error?.message || 'Une erreur est survenue'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            renderTabContent()
          )}
        </main>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
